import React from "react";
import { useGetResponseStatisticsApi } from "../../api/methods/use-get-response-statistics-api";
import { useUnlockOrganizationApi } from "../../api/methods/use-unlock-organization-api";
import { useSearchParams } from "react-router-dom";
import { StatisticsTable } from "./statistics-table";
import { Loader } from "../../components/loader";
import { useSupervisorMode } from "../../components/supervisor-mode-provider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

type TabIndex = "active" | "paused";

export const DashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = React.useState<string>(
    searchParams.get("term") || "",
  );
  const [tabIndex, setTabIndex] = React.useState<TabIndex>("active");

  const { data, isFetching: isLoading } =
    useGetResponseStatisticsApi(searchQuery);
  const isSupervisorMode = useSupervisorMode();
  const unlockOrganization = useUnlockOrganizationApi();
  const { toast } = useToast();

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSearchParams({ term: value });
  };

  const filteredAndSortedData = React.useMemo(() => {
    return (
      data
        ?.filter((row) =>
          tabIndex === "active"
            ? row.attributes.tagging_status !== "pause"
            : row.attributes.tagging_status === "pause",
        )
        .filter(
          (row) => row.attributes.theme_structure !== "Elastic Theme Structure",
        )
        .sort((a, b) => b.attributes.value - a.attributes.value) || []
    );
  }, [data, tabIndex]);

  const handleUnlockComments = React.useCallback(
    (organizationId: number) => {
      unlockOrganization.mutate(
        { organizationId, isSupervisorMode },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Comments unlocked successfully",
            });
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: "Failed to unlock comments: " + error.message,
              variant: "destructive",
            });
          },
        },
      );
    },
    [unlockOrganization, isSupervisorMode, toast],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-4">
        <Input
          placeholder="Search company"
          value={searchQuery}
          onChange={handleSearchQueryChange}
          className="max-w-sm"
        />

        <Tabs
          value={tabIndex}
          onValueChange={(value) => setTabIndex(value as TabIndex)}
        >
          <TabsList>
            <TabsTrigger value="active">Default</TabsTrigger>
            <TabsTrigger value="paused">Paused</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-4">
            {isLoading ? (
              <Loader />
            ) : (
              <StatisticsTable
                data={filteredAndSortedData}
                isSupervisorMode={isSupervisorMode}
                onUnlockComments={handleUnlockComments}
              />
            )}
          </TabsContent>
          <TabsContent value="paused" className="mt-4">
            {isLoading ? (
              <Loader />
            ) : (
              <StatisticsTable
                data={filteredAndSortedData}
                isSupervisorMode={isSupervisorMode}
                onUnlockComments={handleUnlockComments}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
